import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ConversationEntry as ConversationEntryType, markEntryAsOld, updateQuestion, updateEntryFeedback, EntryFeedback } from '../store/conversationSlice';
import QuestionEditModal from './QuestionEditModal';
import FeedbackPopover from './FeedbackPopover';
import { ConversationCategory, getCategoryIcon } from '../types/conversation';
import { apiService } from '../services/apiService';
import { sessionManager } from '../services/sessionManager';
import { useToast } from '../contexts/ToastContext';

interface ConversationEntryProps {
  entry: ConversationEntryType;
}

const ConversationEntry: React.FC<ConversationEntryProps> = ({ entry }) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'stored' | 'ai'>('stored');
  // Collapse answers for NEW_INQUIRY by default to reduce UI clutter
  const [isAnswerExpanded, setIsAnswerExpanded] = useState(
    entry.category !== ConversationCategory.NEW_INQUIRY
  );

  // Mark entry as old after 3 seconds to remove the highlight
  useEffect(() => {
    if (entry.isNew) {
      const timer = setTimeout(() => {
        dispatch(markEntryAsOld(entry.id));
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [entry.isNew, entry.id, dispatch]);
  const getCategoryColor = (category: string) => {
    switch (category) {
      case ConversationCategory.FAQ:
        return 'bg-green-100 text-green-800 border-green-200';
      case ConversationCategory.OBJECTION:
        return 'bg-red-100 text-red-800 border-red-200';
      case ConversationCategory.HESITATION:
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case ConversationCategory.NEW_INQUIRY:
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case ConversationCategory.OTHER:
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500';
      case 'pending':
        return 'bg-orange-500';
      case 'error':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getCategoryText = (category: string) => {
    if(category === ConversationCategory.NEW_INQUIRY)
      return "New Objection/FAQ"
    else
      return category
  };

  const formatAnswer = (answer: string) => {
    // Convert bullet points to JSX
    const lines = answer.split('\n');
    const formattedLines = lines.map((line, index) => {
      // Handle bullet points
      if (line.trim().startsWith('•') || line.trim().startsWith('-') || line.trim().startsWith('*')) {
        return (
          <li key={index} className="ml-3 mb-0.5 text-sm">
            {line.trim().substring(1).trim()}
          </li>
        );
      }
      // Handle regular text
      if (line.trim()) {
        return (
          <p key={index} className="mb-1 text-sm">
            {line.trim()}
          </p>
        );
      }
      return null;
    }).filter(Boolean);

    return <div>{formattedLines}</div>;
  };

  const formatTime = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString('en-US', { 
        hour12: false, 
        hour: '2-digit', 
        minute: '2-digit', 
        second: '2-digit' 
      });
    } catch {
      return 'Invalid time';
    }
  };

  const handleQuestionEdited = (originalQuestion: string, editedQuestion: string, answer: string) => {
    dispatch(updateQuestion({ originalQuestion, editedQuestion, answer }));
  };

  const handleFeedbackSubmit = async (rating: 'up' | 'down', reason: string) => {
    const roomId = sessionManager.getRoomId();

    // Update Redux immediately so the UI reflects the choice even if persist fails
    const feedback: EntryFeedback = {
      rating,
      reason: reason || undefined,
      submittedAt: new Date().toISOString()
    };
    dispatch(updateEntryFeedback({ id: entry.id, feedback }));
    showToast('Feedback recorded');

    if (!roomId) return;

    try {
      await apiService.submitEntryFeedback(roomId, {
        entryType: 'conversation',
        entryTimestamp: entry.timestamp,
        category: entry.category,
        rating,
        reason
      });
    } catch (err) {
      // Persistence failed — feedback stays in Redux (will be included in session save)
      console.warn('[ConversationEntry] Entry feedback persist failed:', err);
    }
  };


  return (
    <div className={`border border-gray-200 rounded-xl p-2 space-y-1.5 shadow-sm hover:shadow-md transition-all duration-3000 ${
      entry.isNew 
        ? 'bg-blue-50 border-blue-300 animate-pulse' 
        : 'bg-white'
    }`}>
      {/* Header with timestamp and status */}
      <div className="flex items-center justify-between">
        <div className="text-xs text-gray-400 font-medium">
          {formatTime(entry.timestamp)}
        </div>
        <div className={`w-3 h-3 rounded-full ${getStatusColor(entry.status)}`} />
      </div>

      {/* Customer Question */}
      <div className="bg-gray-100 rounded-lg p-2">
        <div className="flex items-start justify-between mb-1">
          <h3 className="text-xs font-semibold text-gray-700 flex items-center">
            <span className="mr-1">🙋‍♂️</span>
            CUSTOMER • OBJECTION DETECTED
          </h3>
          <div className="flex items-center gap-1">
            <FeedbackPopover
              currentFeedback={entry.feedback}
              onSubmit={handleFeedbackSubmit}
              disabled={entry.status === 'pending'}
            />
            <button
              onClick={() => setIsEditModalOpen(true)}
              className="text-gray-400 hover:text-blue-600 transition-colors p-1"
              title="Edit question"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
          </div>
        </div>
        <p className="text-sm text-gray-800 font-medium">{entry.question}</p>
      </div>

      {/* Category and Relevance */}
      <div className="flex items-center justify-between">
        <div className={`px-3 py-1 rounded-full text-xs font-medium border ${getCategoryColor(entry.category)}`}>
          {getCategoryIcon(entry.category)} Category: {getCategoryText(entry.category)}
        </div>
        <div className="text-xs text-gray-600">
          🎯 Relevance: <span className="font-semibold">{entry.relevance_score.toFixed(2)}</span>
        </div>
      </div>

      {/* Expand/Collapse Toggle for NEW_INQUIRY */}
      {entry.category === ConversationCategory.NEW_INQUIRY && (
        <button
          onClick={() => setIsAnswerExpanded(!isAnswerExpanded)}
          className="w-full flex items-center justify-center gap-2 py-1.5 text-xs font-medium text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all border border-gray-200"
        >
          <span className="text-base">{isAnswerExpanded ? '▼' : '▶'}</span>
          <span>{isAnswerExpanded ? 'Hide Answers' : 'Show Answers'}</span>
          {!isAnswerExpanded && (
            <span className="ml-1 flex items-center gap-1">
              {entry.stored_answer && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-xs">
                  📚 Stored
                </span>
              )}
              {entry.status === 'completed' && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-green-100 text-green-700 text-xs">
                  🤖 AI
                </span>
              )}
            </span>
          )}
        </button>
      )}

      {/* Answer Section with Tabs */}
      {isAnswerExpanded && (
      <div className="rounded-lg border border-gray-200 overflow-hidden">
        {/* Tab Headers */}
        <div className="flex border-b border-gray-200 bg-gray-50">
          <button
            onClick={() => setActiveTab('stored')}
            className={`flex-1 px-3 py-2 text-xs font-semibold transition-colors ${
              activeTab === 'stored'
                ? 'bg-white text-blue-700 border-b-2 border-blue-700'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
            }`}
          >
            <span className="mr-1">📚</span>
            Stored Answer
            {entry.stored_answer && (
              <span className="ml-1 inline-block w-2 h-2 rounded-full bg-green-500"></span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('ai')}
            className={`flex-1 px-3 py-2 text-xs font-semibold transition-colors ${
              activeTab === 'ai'
                ? 'bg-white text-purple-700 border-b-2 border-purple-700'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
            }`}
          >
            <span className="mr-1">🤖</span>
            AI Answer
            {entry.status === 'completed' && (
              <span className="ml-1 inline-block w-2 h-2 rounded-full bg-green-500"></span>
            )}
            {entry.status === 'pending' && (
              <span className="ml-1 inline-block w-2 h-2 rounded-full bg-orange-500 animate-pulse"></span>
            )}
          </button>
        </div>

        {/* Tab Content */}
        <div className="p-2">
          {activeTab === 'stored' ? (
            /* Stored Answer Tab */
            <div>
              {entry.stored_answer ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
                  <div className="mb-1 font-medium text-blue-700 text-xs flex items-center">
                    <span className="mr-1">💾</span>
                    From Database:
                  </div>
                  <div className="prose prose-sm max-w-none text-sm text-gray-800">
                    {formatAnswer(entry.stored_answer)}
                  </div>
                </div>
              ) : entry.category === ConversationCategory.NEW_INQUIRY ? (
                <div className="text-sm text-gray-500 italic flex items-center justify-center py-4 bg-gray-50 rounded-lg">
                  <span className="mr-1">📝</span>
                  No stored answer - this is a new objection/FAQ not yet in the knowledge base
                </div>
              ) : (
                <div className="text-sm text-gray-500 italic flex items-center justify-center py-4">
                  <span className="mr-1">⏳</span>
                  Loading stored answer...
                </div>
              )}
            </div>
          ) : (
            /* AI Answer Tab */
            <div className={`${
              entry.status === 'pending'
                ? 'bg-orange-50 border border-orange-200'
                : entry.status === 'error'
                  ? 'bg-red-50 border border-red-200'
                  : 'bg-green-50 border border-green-200'
            } rounded-lg p-2`}>
              {entry.status === 'pending' ? (
                <div className="text-sm text-gray-600 italic flex items-center justify-center py-4">
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 border-2 border-orange-600 border-t-transparent rounded-full animate-spin" />
                    <span>AI is processing your question...</span>
                  </div>
                </div>
              ) : entry.status === 'error' ? (
                <div className="text-sm text-red-700">
                  <span className="mr-1">❌</span>
                  {entry.error || 'An error occurred while generating the response'}
                </div>
              ) : (
                <div className="text-gray-800">
                  <div className="mb-1 font-medium text-green-700 text-xs">
                    <span className="mr-1">💡</span>
                    AI Generated Answer:
                  </div>
                  <div className="prose prose-sm max-w-none text-sm">
                    {entry.answer && formatAnswer(entry.answer)}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      )}

      {/* Edit Question Modal */}
      <QuestionEditModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        originalQuestion={entry.question}
        onQuestionEdited={handleQuestionEdited}
      />
    </div>
  );
};

export default ConversationEntry;